import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, Send, Lock, Shield, User, Clock, Info, X, Unlock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import AttachmentPicker, { UploadedAttachment } from "@/components/support/AttachmentPicker";
import AttachmentList, { AttachmentMeta } from "@/components/support/AttachmentList";
import SupportReviewDialog from "@/components/support/SupportReviewDialog";

interface SupportMessage {
  id: string;
  ticket_id: string;
  sender_id: string;
  sender_role: "user" | "admin";
  content: string;
  attachments: AttachmentMeta[];
  created_at: string;
  delivered_at: string | null;
  read_at: string | null;
}

const fmtDT = (v: string) =>
  new Date(v).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

interface SupportTicket {
  id: string;
  user_id: string;
  subject: string;
  status: "open" | "pending_admin" | "pending_user" | "closed";
  opened_at: string;
  closed_at: string | null;
}

interface Props {
  ticket: SupportTicket;
  asAdmin?: boolean;
  onClose?: () => void;
  onTicketChange?: (t: SupportTicket) => void;
  compact?: boolean;
}

const SupportChatWindow = ({ ticket, asAdmin = false, onClose, onTicketChange, compact }: Props) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [hasReview, setHasReview] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentTicket, setCurrentTicket] = useState(ticket);

  useEffect(() => setCurrentTicket(ticket), [ticket]);

  const isClosed = currentTicket.status === "closed";

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    supabase
      .from("support_messages")
      .select("*")
      .eq("ticket_id", ticket.id)
      .order("created_at", { ascending: true })
      .then(async ({ data }) => {
        const list = ((data ?? []) as unknown) as SupportMessage[];
        if (mounted) setMessages(list);
        setLoading(false);
        // Mark incoming messages (from the other party) as delivered + read now
        const incoming = list.filter((m) =>
          asAdmin ? m.sender_role === "user" : m.sender_role === "admin",
        );
        const toDeliver = incoming.filter((m) => !m.delivered_at).map((m) => m.id);
        const toRead = incoming.filter((m) => !m.read_at).map((m) => m.id);
        const nowIso = new Date().toISOString();
        if (toDeliver.length) {
          await supabase.from("support_messages").update({ delivered_at: nowIso }).in("id", toDeliver);
        }
        if (toRead.length) {
          await supabase.from("support_messages").update({ read_at: nowIso }).in("id", toRead);
        }
      });

    supabase
      .from("support_reviews")
      .select("id")
      .eq("ticket_id", ticket.id)
      .maybeSingle()
      .then(({ data }) => mounted && setHasReview(!!data));

    const channel = supabase
      .channel(`support-ticket-${ticket.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "support_messages", filter: `ticket_id=eq.${ticket.id}` },
        async (payload) => {
          const msg = payload.new as SupportMessage;
          setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
          const incoming = asAdmin ? msg.sender_role === "user" : msg.sender_role === "admin";
          if (incoming) {
            const nowIso = new Date().toISOString();
            await supabase
              .from("support_messages")
              .update({ delivered_at: nowIso, read_at: nowIso })
              .eq("id", msg.id);
          }
        },
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "support_messages", filter: `ticket_id=eq.${ticket.id}` },
        (payload) => {
          const msg = payload.new as SupportMessage;
          setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, ...msg } : m)));
        },
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "support_tickets", filter: `id=eq.${ticket.id}` },
        (payload) => {
          const next = payload.new as SupportTicket;
          setCurrentTicket(next);
          onTicketChange?.(next);
        },
      )
      .subscribe();

    return () => {
      mounted = false;
      supabase.removeChannel(channel);
    };
  }, [ticket.id, onTicketChange]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  const send = async () => {
    if (!content.trim() && attachments.length === 0) return;
    if (isClosed) {
      toast.error("Conversation clôturée");
      return;
    }
    setSending(true);
    // Mark all incoming messages as delivered + read BEFORE sending our reply,
    // otherwise timestamps would be set only when we next open the window.
    const nowIso = new Date().toISOString();
    const incoming = messages.filter((m) =>
      asAdmin ? m.sender_role === "user" : m.sender_role === "admin",
    );
    const toDeliver = incoming.filter((m) => !m.delivered_at).map((m) => m.id);
    const toRead = incoming.filter((m) => !m.read_at).map((m) => m.id);
    if (toDeliver.length) {
      await supabase.from("support_messages").update({ delivered_at: nowIso }).in("id", toDeliver);
    }
    if (toRead.length) {
      await supabase.from("support_messages").update({ read_at: nowIso }).in("id", toRead);
    }
    const { error } = await supabase.functions.invoke("support-send-message", {
      body: {
        ticket_id: ticket.id,
        content: content.trim(),
        attachments,
      },
    });

    setSending(false);
    if (error) {
      console.error(error);
      toast.error("Envoi échoué");
      return;
    }
    setContent("");
    setAttachments([]);
  };

  const closeTicket = async () => {
    if (!confirm("Clôturer la conversation ? Les messages resteront archivés mais plus aucune réponse ne sera possible.")) return;
    setClosing(true);
    const { error } = await supabase.functions.invoke("support-close-ticket", {
      body: { ticket_id: ticket.id },
    });
    setClosing(false);
    if (error) {
      toast.error("Clôture échouée");
      return;
    }
    toast.success("Conversation clôturée");
    if (!asAdmin && !hasReview) setReviewOpen(true);
  };

  const reopenTicket = async () => {
    if (!confirm("Rouvrir cette conversation support ? L'utilisateur sera notifié et pourra à nouveau répondre.")) return;
    setReopening(true);
    const { error } = await supabase.functions.invoke("support-reopen-ticket", {
      body: { ticket_id: ticket.id },
    });
    setReopening(false);
    if (error) {
      toast.error("Réouverture échouée");
      return;
    }
    toast.success("Conversation rouverte");
  };

  return (
    <div className={`flex flex-col ${compact ? "h-[70vh]" : "h-[calc(100vh-12rem)] min-h-[500px]"} bg-background rounded-2xl border border-border overflow-hidden`}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-card">
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold truncate flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary shrink-0" />
            {currentTicket.subject}
          </h3>
          <div className="flex items-center gap-2 mt-0.5">
            <Badge
              variant="outline"
              className={
                currentTicket.status === "closed"
                  ? "bg-muted text-muted-foreground"
                  : currentTicket.status === "pending_admin"
                    ? "bg-amber-500/15 text-amber-600 border-amber-500/30"
                    : "bg-green-500/15 text-green-600 border-green-500/30"
              }
            >
              {currentTicket.status === "closed"
                ? "Clôturée"
                : currentTicket.status === "pending_admin"
                  ? "En attente du support"
                  : currentTicket.status === "pending_user"
                    ? "En attente de vous"
                    : "Ouverte"}
            </Badge>
            <span className="text-xs text-muted-foreground hidden sm:inline">
              Ouverte le {new Date(currentTicket.opened_at).toLocaleDateString("fr-FR")}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {!isClosed && (
            <Button
              variant="outline"
              size="sm"
              onClick={closeTicket}
              disabled={closing}
              className="rounded-lg"
            >
              {closing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4 sm:mr-1.5" />}
              <span className="hidden sm:inline">Clôturer</span>
            </Button>
          )}
          {isClosed && asAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={reopenTicket}
              disabled={reopening}
              className="rounded-lg"
            >
              {reopening ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4 sm:mr-1.5" />}
              <span className="hidden sm:inline">Rouvrir</span>
            </Button>
          )}
          {isClosed && !asAdmin && !hasReview && (
            <Button size="sm" onClick={() => setReviewOpen(true)} className="rounded-lg">
              ⭐ Évaluer
            </Button>
          )}
          {onClose && (
            <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Info banner */}
      <div className="px-4 py-2 bg-primary/5 border-b border-primary/10 text-xs text-muted-foreground flex items-center gap-2">
        <Info className="w-3.5 h-3.5 text-primary shrink-0" />
        <span>Délai de réponse moyen : <strong>jusqu'à 24h</strong>. Notifications par cloche + email à chaque réponse.</span>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 sm:px-4 py-4 space-y-3 bg-muted/20">
        {loading ? (
          <div className="text-center py-10">
            <Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-10 text-sm text-muted-foreground">
            Démarrez la conversation. Notre équipe vous répond sous 24h.
          </div>
        ) : (
          messages.map((m) => {
            const mine = asAdmin ? m.sender_role === "admin" : m.sender_id === user?.id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] sm:max-w-[75%] rounded-2xl px-3.5 py-2.5 ${
                    mine
                      ? "bg-primary text-primary-foreground"
                      : m.sender_role === "admin"
                        ? "bg-card border border-primary/30"
                        : "bg-card border border-border"
                  }`}
                >
                  <div className="flex items-center gap-1.5 mb-1 text-[11px] opacity-80">
                    {m.sender_role === "admin" ? (
                      <Shield className="w-3 h-3" />
                    ) : (
                      <User className="w-3 h-3" />
                    )}
                    <span className="font-semibold">
                      {m.sender_role === "admin" ? "Équipe support" : mine ? "Vous" : "Utilisateur"}
                    </span>
                    <span>·</span>
                    <Clock className="w-3 h-3" />
                    <span>{new Date(m.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                  </div>
                  {m.content && (
                    <p className="text-sm whitespace-pre-wrap break-words">{m.content}</p>
                  )}
                  {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                    <div className={mine ? "[&_li]:bg-white/10 [&_li]:border-white/20" : ""}>
                      <AttachmentList attachments={m.attachments} bucket="support-attachments" />
                    </div>
                  )}
                  {mine && (
                    <div className="mt-1.5 text-[10px] opacity-70 flex flex-col items-end gap-0.5">
                      <span>✓ Envoyé le {fmtDT(m.created_at)}</span>
                      {m.delivered_at && (
                        <span>✓✓ Distribué le {fmtDT(m.delivered_at)}</span>
                      )}
                      {m.read_at && (
                        <span className="font-semibold">✓✓ Lu le {fmtDT(m.read_at)}</span>
                      )}
                    </div>
                  )}
                  {asAdmin && !mine && (
                    <div className="mt-1.5 text-[10px] opacity-70 flex flex-col items-start gap-0.5">
                      <span>Envoyé le {fmtDT(m.created_at)}</span>
                      {m.delivered_at && <span>Distribué le {fmtDT(m.delivered_at)}</span>}
                      {m.read_at && <span>Lu le {fmtDT(m.read_at)}</span>}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Composer */}
      {isClosed ? (
        <div className="p-4 border-t border-border bg-muted/40 text-center text-sm text-muted-foreground">
          <Lock className="w-4 h-4 inline mr-1.5" />
          Conversation clôturée — archivée pour traçabilité.
        </div>
      ) : (
        <div className="p-3 border-t border-border bg-card space-y-2">
          <AttachmentPicker
            bucket="support-attachments"
            value={attachments}
            onChange={setAttachments}
            disabled={sending}
            compact
          />
          <div className="flex items-end gap-2">
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Écrivez votre message…"
              rows={2}
              maxLength={5000}
              disabled={sending}
              className="flex-1 resize-none"
            />
            <Button onClick={send} disabled={sending || (!content.trim() && attachments.length === 0)} className="rounded-xl">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground text-right">Ctrl/⌘ + Entrée pour envoyer</p>
        </div>
      )}

      <SupportReviewDialog
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        ticketId={ticket.id}
        onSubmitted={() => setHasReview(true)}
      />
    </div>
  );
};

export default SupportChatWindow;
